import { useEffect, useState } from 'react';
import { Button, Flex, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { Logger } from 'src/utils/Logger';
import CommunitySkillsTab from './CommunitySkillsTab';
import CuratedSkillsTab from './CuratedSkillsTab';
import CompanySkillsTab from './CompanySkillsTab';
import useSkillLibrary from './hooks/useSkillLibrary';
import SkillPreviewModal from './SkillPreviewModal';
import SkillEditorModal from '../manage-siesta-skills/SkillEditorModal';
import useTenantSkills from '../manage-siesta-skills/hooks/useTenantSkills';
import useTenantSkill from '../manage-siesta-skills/hooks/useTenantSkill';

enum SkillLibraryTab {
  Company = 'company',
  Curated = 'curated',
  Community = 'community',
}

type EditorState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; skillId: string };

export default function SkillLibraryPage() {
  // Only a company that can publish has anything to find in Community; for the rest it is an
  // empty tab inviting them to share.
  const { flagEnabledSkillLibraryPublishing } = useFlags();
  const showCommunity = flagEnabledSkillLibraryPublishing === true;
  const [activeTab, setActiveTab] = useState<SkillLibraryTab>(SkillLibraryTab.Company);
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'closed' });
  const [previewEntryId, setPreviewEntryId] = useState<string | null>(null);

  const {
    data,
    error,
    isLoading: isLibraryLoading,
    isError: isLibraryError,
    refetch,
  } = useSkillLibrary();
  // Same query key as the tab's own read, so this costs no extra request.
  const { data: skills, isLoading, isError } = useTenantSkills();

  const closeEditor = () => setEditorState({ mode: 'closed' });

  const editingSkillId = editorState.mode === 'edit' ? editorState.skillId : null;
  const { freshSkill: editingSkill } = useTenantSkill(editingSkillId, closeEditor);

  // Logged rather than surfaced: the company's own skills do not depend on this request, so a
  // failure costs the tab its name and nothing else.
  useEffect(() => {
    if (error) Logger.error('Failed to load the skill library', error);
  }, [error]);

  const companyName = data?.companyName ?? null;
  const isEmpty = !isLoading && !isError && skills?.length === 0;
  const onCompanyTab = activeTab === SkillLibraryTab.Company;
  const previewEntry =
    [...(data?.curated.items ?? []), ...(data?.community.items ?? [])].find(
      e => e.id === previewEntryId,
    ) ?? null;

  return (
    <Flex direction="column" gap="lg">
      <SkillEditorModal
        opened={editorState.mode !== 'closed'}
        loading={editorState.mode === 'edit' && editingSkill == null}
        skill={editorState.mode === 'edit' ? (editingSkill ?? null) : null}
        onClose={closeEditor}
      />

      <UnderlineTabs
        mode="local"
        value={activeTab}
        onChange={value => setActiveTab(value as SkillLibraryTab)}
        leadingContent={
          <Title order={6} fw={600}>
            Skill library
          </Title>
        }
        trailingContent={
          onCompanyTab ? (
            <Button
              variant={isEmpty ? 'default' : 'filled'}
              leftSection={<IconPlus size={16} />}
              onClick={() => setEditorState({ mode: 'create' })}
            >
              Create skill
            </Button>
          ) : undefined
        }
        tabs={[
          {
            label: companyName ? `${companyName}'s skills` : 'Your skills',
            value: SkillLibraryTab.Company,
          },
          { label: 'Curated skills', value: SkillLibraryTab.Curated },
          ...(showCommunity
            ? [{ label: 'Community skills', value: SkillLibraryTab.Community }]
            : []),
        ]}
      />
      {onCompanyTab && (
        <CompanySkillsTab
          companyName={companyName}
          onBrowseCurated={() => setActiveTab(SkillLibraryTab.Curated)}
          onCreate={() => setEditorState({ mode: 'create' })}
          onEdit={skillId => setEditorState({ mode: 'edit', skillId })}
        />
      )}
      {activeTab === SkillLibraryTab.Curated && (
        <CuratedSkillsTab
          page={data?.curated}
          isLoading={isLibraryLoading}
          isError={isLibraryError}
          onRetry={() => refetch()}
          onOpen={setPreviewEntryId}
          companyName={companyName}
        />
      )}
      {showCommunity && activeTab === SkillLibraryTab.Community && (
        <CommunitySkillsTab
          page={data?.community}
          isLoading={isLibraryLoading}
          isError={isLibraryError}
          onRetry={() => refetch()}
          onOpen={setPreviewEntryId}
        />
      )}
      <SkillPreviewModal
        entry={previewEntry}
        companyName={companyName}
        onClose={() => setPreviewEntryId(null)}
      />
    </Flex>
  );
}
